import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import { QUERY_KEYS } from './queryKeys';

export interface WeeklyReport {
    id: number;
    week_start: string;
    week_end: string;
    technician_name?: string;
    status: 'draft' | 'submitted' | 'approved' | 'rejected';
    status_display: string;
    created_at: string;
    updated_at: string;
}

const fetchWeeklyReports = async (): Promise<WeeklyReport[]> => {
    const { data } = await api.get(API_ROUTES.weeklyReports);
    return data;
};

export const useWeeklyReportsList = () => {
    return useQuery({
        queryKey: QUERY_KEYS.weeklyReports.all,
        queryFn: fetchWeeklyReports,
    });
};

const createWeeklyReport = async (data: any) => {
    const { data: response } = await api.post(API_ROUTES.weeklyReports, data);
    return response;
};

export const useCreateWeeklyReport = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createWeeklyReport,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weeklyReports.all });
        },
    });
};

const fetchWeeklyReportDetail = async (id: string | number) => {
    const { data } = await api.get(API_ROUTES.weeklyReportDetail(id));
    return data;
};

export const useWeeklyReportDetail = (id: string | number) => {
    return useQuery({
        queryKey: QUERY_KEYS.weeklyReports.detail(id),
        queryFn: () => fetchWeeklyReportDetail(id),
        enabled: !!id,
    });
};

const updateWeeklyReport = async ({ id, data }: { id: string | number; data: any }) => {
    const { data: response } = await api.put(API_ROUTES.weeklyReportDetail(id), data);
    return response;
};

export const useUpdateWeeklyReport = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateWeeklyReport,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weeklyReports.all });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.weeklyReports.detail(variables.id) });
        },
    });
};
